'use client';

import { useEffect } from 'react';

/**
 * Swap the document's browser-tab favicon (`<link rel="icon">`) to `url`
 * while the calling component is mounted, restoring the previous favicon on
 * unmount or when `url` becomes empty.
 *
 * Used on org-branded surfaces (admin shell, customer referral pages, and the
 * branded admin-login / customer-login / remote-auth pages) so each
 * organisation's custom favicon (`organizations.favicon_url`) shows in the
 * browser tab. A null/empty `url` leaves the default favicon
 * (`app/favicon.ico`, auto-injected by the Next App Router) in place.
 *
 * All matching `link[rel~="icon"]` elements are swapped — the App Router can
 * emit more than one (e.g. `icon` plus `shortcut icon`) and some browsers
 * pick the last, so updating only the first could leave a stale icon
 * (MR !665 review, point 4). `apple-touch-icon` is a distinct rel token and
 * is intentionally left untouched.
 */
export function useFavicon(url: string | null | undefined): void {
  useEffect(() => {
    if (!url || typeof document === 'undefined') return;

    const head = document.head;
    let links = Array.from(head.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'));
    // Track a link we created so cleanup removes it instead of restoring it —
    // we never want to leave an orphan link behind.
    let created: HTMLLinkElement | null = null;
    if (links.length === 0) {
      created = document.createElement('link');
      created.rel = 'icon';
      head.appendChild(created);
      links = [created];
    }

    // Hint the MIME type for ICO/SVG so browsers pick the right decoder.
    const type = /\.svg($|\?)/i.test(url)
      ? 'image/svg+xml'
      : /\.ico($|\?)/i.test(url)
        ? 'image/x-icon'
        : null;

    const snapshots = links.map((link) => ({
      link,
      href: link.getAttribute('href'),
      type: link.getAttribute('type'),
    }));

    for (const link of links) {
      link.setAttribute('href', url);
      if (type) link.setAttribute('type', type);
      else link.removeAttribute('type');
    }

    return () => {
      if (created) created.remove();
      for (const snap of snapshots) {
        if (snap.link === created) continue;
        if (snap.href !== null) snap.link.setAttribute('href', snap.href);
        else snap.link.removeAttribute('href');
        if (snap.type !== null) snap.link.setAttribute('type', snap.type);
        else snap.link.removeAttribute('type');
      }
    };
  }, [url]);
}
